/**
 * Authentication Hook
 *
 * React hook that provides access to authentication context.
 * This is a simple wrapper around the AuthContext that provides a clean
 * interface for components to access authentication state and operations.
 *
 * All authentication logic lives in AuthProvider (src/providers/AuthProvider.tsx).
 * This hook simply consumes the context and provides type-safe access.
 *
 * Usage:
 * ```tsx
 * const { authState, login, logout, isAuthenticated, user } = useAuth();
 *
 * // Check authentication status
 * if (isAuthenticated) {
 *   return <DashboardComponent user={user} />;
 * }
 *
 * // Perform login
 * const handleLogin = async (email: string, password: string) => {
 *   const result = await login({ email, password });
 *   if (result.success) {
 *     // Login successful
 *   } else {
 *     // Handle error: result.message
 *   }
 * };
 * ```
 */

import { useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import type { UseAuthReturn } from '@/types/auth';

/**
 * Authentication Hook
 *
 * Provides authentication state and operations for React components.
 * Must be used within an AuthProvider component tree.
 *
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider. ' +
      'Make sure your component tree is wrapped with <AuthProvider>.'
    );
  }

  return context;
}

/**
 * Default export for easy importing
 */
export default useAuth;
